import mixpanel from "mixpanel-browser";
import { useRive } from "@rive-app/react-canvas";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageSquare, Send, Clock, History, Smile, Paperclip, FileText, CheckCircle2, FileText as FileTextIcon, AlertTriangle, Sparkles, ChevronLeft, User, WifiOff, Megaphone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useInstagramChats } from "@/hooks/useInstagramChats";
import { ChatMessage } from "@/components/ChatMessage";
import { ChatList, type OperatorStatus } from "@/components/ChatList";
import { ChatHeader } from "@/components/ChatHeader";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { supabase, chatSupabase} from '@/integrations/supabase/client';
import { useTags } from "@/hooks/useTags";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { normalizePhone } from "@/utils/phoneUtils";
import { buildOperatorMessagePayload, sendOperatorMessageToWebhook } from "@/utils/operatorMessageWebhook";
import { useChatNotifications } from "@/hooks/useChatNotifications";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useQueueAutoDistribution } from "@/hooks/useQueueAutoDistribution";
import { useMacros } from "@/hooks/useMacros";
import { MacroMenu } from "@/components/MacroMenu";
import { MacrosManagerDialog } from "@/components/MacrosManagerDialog";
import { MacroDialog } from "@/components/MacroDialog";
import type { Macro } from "@/hooks/useMacros";
import { ChatDisparoTemplateDialog } from "@/components/ChatDisparoTemplateDialog";

const COMMON_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
  '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
  '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
  '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
  '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖',
  '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏',
  '🙌', '👐', '🤲', '🤝', '🙏', '💪', '❤️', '💛',
  '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️',
  '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟',
  '😮', '😯', '😲', '😳', '🥵', '🥶', '😱', '😨',
  '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥',
  '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
  '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
  '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑',
  '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻',
  '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸',
  '😹', '😻', '😼', '😽', '🙀', '😿', '😾'
];

function ChatEmptyState() {
  const riveRef = useRef<any>(null);

  const { RiveComponent, rive } = useRive({
    src: "/logos/2625-5362-chat.riv",
    autoplay: true,
    onStop: () => {
      setTimeout(() => riveRef.current?.play(), 0);
    },
  });

  useEffect(() => {
    riveRef.current = rive;
    if (rive) rive.play();
  }, [rive]);

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <div className="w-44 h-44">
        <RiveComponent width={176} height={176} />
      </div>
      <h3 className="text-lg font-semibold">Selecione uma conversa</h3>
      <p className="text-sm text-muted-foreground text-center max-w-[240px]">
        Escolha uma conversa para começar a visualizar as mensagens.
      </p>
    </div>
  );
}

export default function Chats() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Fonte unificada: todos os chats e mensagens de chats_Instagram + chat_messages_instagram (todos os canais)
  const unifiedHook = useInstagramChats();

  const selectedChat = unifiedHook.selectedChat;
  const chatChannel = ((selectedChat as any)?.channel ?? "").toString().toLowerCase();
  const isInstagramMode = !!selectedChat && chatChannel === "instagram";
  /** Instagram e Messenger (Facebook): apenas imagem ou vídeo; sem documento/áudio */
  const isImageVideoOnlyMode = !!selectedChat && (chatChannel === "instagram" || chatChannel === "messenger");

  const {
    instances,
    instancesLoading,
    selectedInstance,
    setSelectedInstance,
    selectedChatMessages,
    searchQuery,
    setSearchQuery,
    refetchMessages,
    showAllMessages,
    setShowAllMessages,
    isMessagesLoading: isMessagesLoadingFromHook,
  } = unifiedHook;

  const chats = unifiedHook.chats || [];
  const chatsLoading = unifiedHook.chatsLoading;
  const setSelectedChat = unifiedHook.setSelectedChat;
  const refetchChats = unifiedHook.refetchChats;
  const verifyChats = unifiedHook.verifyChats;

  // Sincronizar chat selecionado com a URL (query param ?chatId=...)
  useEffect(() => {
    if (selectedChat?.id) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (newParams.get('chatId') !== selectedChat.id) {
          newParams.set('chatId', selectedChat.id);
          return newParams;
        }
        return prev;
      }, { replace: true });
    } else {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (newParams.has('chatId')) {
          newParams.delete('chatId');
          return newParams;
        }
        return prev;
      }, { replace: true });
    }
  }, [selectedChat?.id, setSearchParams]);

  // Resetar override de instância quando mudar de chat
  useEffect(() => {
    setOverrideInstanceId(null);
  }, [selectedChat?.id]);

  // Quando não há filtro por instância (Todas), usar a instância do chat selecionado para envio e ações
  const effectiveInstanceId = selectedInstance ?? (selectedChat as any)?.instance_id ?? null;

  const { currentOrg } = useOrganizationContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const unreadMarkerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isUserScrollingRef = useRef(false); // Rastrear se o usuário está rolando manualmente
  const shouldAutoScrollRef = useRef(true); // Controlar se deve fazer auto-scroll
  const lastScrollTopRef = useRef(0); // Rastrear última posição de scroll
  const [messageText, setMessageText] = useState("");
  const [noteText, setNoteText] = useState(""); // Texto separado para anotação interna
  const [sendingMessage, setSendingMessage] = useState(false);
  const [operatorStatus, setOperatorStatus] = useState<OperatorStatus>(() => {
    const saved = localStorage.getItem('fluity_operator_status')
    return (saved as OperatorStatus) ?? "offline"
  });

  const handleOperatorStatusChange = async (newStatus: OperatorStatus) => {
    const prevStatus = operatorStatus;
    setOperatorStatus(newStatus);
    localStorage.setItem('fluity_operator_status', newStatus);
    mixpanel.track('app_operator_status_changed', { from: prevStatus, to: newStatus });
    if (!user?.id || !currentOrg?.id) return;
    try {
      const { error } = await supabase.rpc('insert_user_event', {
        p_event_type: 'operator_status_changed',
        p_org_id:     currentOrg.id,
        p_user_id:    user.id,
        p_metadata:   { from: prevStatus, to: newStatus },
      });
      if (error) console.error('Erro ao registrar evento de status:', error);
    } catch (err) {
      console.error('Erro ao registrar evento de status:', err);
    }
  };
  const [localMessages, setLocalMessages] = useState<any[]>([]);
  const { data: userProfile = null } = useQuery({
    queryKey: ["my-profile", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', user!.id)
        .maybeSingle();
      return data ?? null;
    },
  });
  const [contactTags, setContactTags] = useState<Record<string, any[]>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isInternalNoteMode, setIsInternalNoteMode] = useState(false);
  const [lastUnreadMessageId, setLastUnreadMessageId] = useState<string | null>(null);
  const [localUnreadCount, setLocalUnreadCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showMobileConversation, setShowMobileConversation] = useState(false);
  const [messagesLimit, setMessagesLimit] = useState(30); // Limite inicial de mensagens (otimizado)
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [, setIsLoadingMessages] = useState(false); // kept for setIsLoadingMessages(false) calls below
  const isLoadingMessages = isMessagesLoadingFromHook;
  const [overrideInstanceId, setOverrideInstanceId] = useState<string | null>(null);
  const [showDisparoTemplateDialog, setShowDisparoTemplateDialog] = useState(false);
  const prevSelectedChatIdRef = useRef<string | null>(null);
  const chatLoadStartRef = useRef<number | null>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const lastScrollChatStateRef = useRef<{ chatId: string | null; count: number }>({ chatId: null, count: 0 });
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerificationLog, setShowVerificationLog] = useState(false);
  const [verificationLogs, setVerificationLogs] = useState<string[]>([]);
  
  // Macros states
  const { macros, replaceMacroVariables } = useMacros();
  const [showMacroMenu, setShowMacroMenu] = useState(false);
  const [macroSearchQuery, setMacroSearchQuery] = useState('');
  const [showMacrosManager, setShowMacrosManager] = useState(false);
  const [showCreateMacroDialog, setShowCreateMacroDialog] = useState(false);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Dados de encerramento: sempre da tabela unificada (chats_Instagram) — closed_at, user_closed_id, notes_closed
  const closedInfo = useMemo(() => {
    if (!selectedChat) return null;
    const full = (selectedChat as any).full ?? selectedChat;
    const closed_at = full?.closed_at ?? (selectedChat as any).closed_at;
    if (closed_at == null) return null;
    const user_closed_id = full?.user_closed_id ?? (selectedChat as any).user_closed_id;
    const notes_closed = full?.notes_closed ?? (selectedChat as any).notes_closed;
    return { closed_at, user_closed_id: user_closed_id ?? null, notes_closed: notes_closed ?? null };
  }, [selectedChat]);
  const closedUserId = closedInfo?.user_closed_id ?? null;

  const { data: closerProfile } = useQuery({
    queryKey: ["profile", closedUserId],
    queryFn: async () => {
      if (!closedUserId) return null;
      const { data } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", closedUserId)
        .single();
      return data;
    },
    enabled: !!closedUserId,
  });

  // Função helper para verificar se a instância selecionada é do tipo WhatsApp e tem o agente habilitado
  const isSelectedInstanceWhatsApp = useMemo(() => {
    // Instagram isolated path - Retornar false para Instagram
    if (isInstagramMode) {
      return false;
    }
    
    if (!effectiveInstanceId || !instances || instances.length === 0) {
      return false;
    }
    const selectedInstanceData = instances.find(inst => inst.id === effectiveInstanceId);
    if (!selectedInstanceData) {
      return false;
    }
    // Verificar se é WhatsApp
    const isWhatsApp = selectedInstanceData.channel === 'whatsapp' || 
                       selectedInstanceData.integration_type === 'WHATSAPP-BAILEYS';
    // Verificar se o toggle agent_enabled está ligado
    const isAgentEnabled = selectedInstanceData.agent_enabled === true;
    // Só permitir envio se for WhatsApp E o agente estiver habilitado
    return isWhatsApp && isAgentEnabled;
  }, [effectiveInstanceId, instances, isInstagramMode]);

  // Detectar se a instância original do chat está desativada ou excluída
  const chatOwnInstanceId = useMemo(() => {
    if (!selectedChat) return null;
    return (selectedChat as any)?.instance_id ?? (selectedChat as any)?.full?.instance_id ?? null;
  }, [selectedChat]);

  const chatOwnInstanceData = useMemo(() => {
    if (!chatOwnInstanceId || !instances) return null;
    return instances.find(inst => inst.id === chatOwnInstanceId) ?? null;
  }, [chatOwnInstanceId, instances]);

  const isChatInstanceProblematic = useMemo(() => {
    if (!selectedChat || isInstagramMode) return false;
    if (!chatOwnInstanceId) return false;
    if (!chatOwnInstanceData) return true; // Não encontrada = excluída
    const activeStatuses = ['open', 'not_validated'];
    return chatOwnInstanceData.status === 'deleted' || !activeStatuses.includes(chatOwnInstanceData.status);
  }, [selectedChat, chatOwnInstanceId, chatOwnInstanceData, isInstagramMode]);

  const isZenviaOrMetaInstance = useMemo(() => {
    const type = (chatOwnInstanceData?.integration_type ?? '').toLowerCase();
    return ['whatsapp-zenvia', 'whatsapp-meta'].includes(type);
  }, [chatOwnInstanceData]);

  const isBlockedByDisparo = useMemo(() => {
    if (!isZenviaOrMetaInstance || isInternalNoteMode || isInstagramMode || !selectedChat) return false;
    if (!localMessages.length) return false;
    let lastDisparoIdx = -1;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      if ((localMessages[i] as any).message_type === 'disparo') {
        lastDisparoIdx = i;
        break;
      }
    }
    if (lastDisparoIdx === -1) return false;
    const isUuidCheck = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    for (let i = lastDisparoIdx + 1; i < localMessages.length; i++) {
      const m = localMessages[i] as any;
      const msgUserSender = String(m.user_sender || '').trim();
      const msgAgentId = String(m.agent_id || '').trim();
      const msgOrigem = String(m.origem_contato || '').trim();
      // Detectar mensagem do cliente com a mesma lógica usada em isClientMsg:
      // agent_id UUID = operador da plataforma; user_sender UUID = idem
      const isCustomerMsg = !isUuidCheck(msgAgentId) && !isUuidCheck(msgUserSender) && (
        m.sender_role === 'customer' ||
        m.sender_type === 'contact' ||
        m.sender_type === 'user' ||
        m.message_type === 'user' ||
        m.message_type === 'messages.upsert' ||
        !msgUserSender ||
        msgUserSender === 'Cliente' ||
        (msgOrigem !== '' && msgUserSender === msgOrigem)
      );
      if (isCustomerMsg) return false;
    }
    return true;
  }, [isZenviaOrMetaInstance, isInternalNoteMode, isInstagramMode, selectedChat, localMessages]);

  const activeInstancesForOverride = useMemo(() => {
    if (!instances || !currentOrg?.id) return [];
    // Preferir canal da instância; quando excluída (não encontrada), usar o canal do próprio chat
    const originalChannel = chatOwnInstanceData?.channel
      ?? (selectedChat as any)?.channel
      ?? (selectedChat as any)?.full?.channel
      ?? null;
    const originalIntegrationType = chatOwnInstanceData?.integration_type ?? null;
    return instances.filter(inst => {
      if (inst.org_id !== currentOrg.id || inst.status !== 'open') return false;
      if (originalChannel) return inst.channel === originalChannel;
      if (originalIntegrationType) return inst.integration_type === originalIntegrationType;
      return false; // sem info de canal: não exibe nada para evitar canal errado
    });
  }, [instances, currentOrg?.id, chatOwnInstanceData, selectedChat]);

  // Hook para notificações de chat (lista unificada: buscar por id ou phone)
  const handleNotificationChatSelect = useCallback(
    (chatId: string, phone: string) => {
      const normalizedTargetPhone = normalizePhone(phone);
      const chat =
        chats.find((c) => c.id === chatId) ||
        chats.find((c) => normalizePhone((c as any).phone) === normalizedTargetPhone);
      if (chat) {
        setSelectedChat(chat);
      } else {
        console.warn("⚠️ Chat não encontrado na lista local ao clicar na notificação:", phone);
      }
    },
    [chats, setSelectedChat]
  );

  useChatNotifications(
    selectedChat?.id || null,
    selectedChat?.phone || null,
    handleNotificationChatSelect
  );

  // Hook para distribuição automática da fila (verifica periodicamente)
  useQueueAutoDistribution();
  useEffect(() => { mixpanel.track('app_chats_view') }, [])

  // Função para inserir emoji no campo de texto
  const insertEmoji = (emoji: string) => {
    mixpanel.track('app_chats_emoji_inserted')
    if (isInternalNoteMode) {
      setNoteText(prev => prev + emoji);
    } else {
      setMessageText(prev => prev + emoji);
    }
  };

  // Handler para selecionar macro
  const handleMacroSelect = (macroContent: string) => {
    mixpanel.track('app_chats_macro_selected')
    setMessageText(macroContent);
    setShowMacroMenu(false);
    setMacroSearchQuery('');
    // Focar no input após inserir macro
    messageInputRef.current?.focus();
  };
  
  // Upload de arquivo para o bucket chat-media e retorno de URL pública
  const uploadChatFile = async (file: File, orgId: string, chatKey: string) => {
    const fileExt = file.name.split('.').pop();
    const safeExt = fileExt ? `.${fileExt}` : '';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}${safeExt}`;
    const filePath = `${orgId}/chats/${chatKey}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('chat-media')
      .upload(filePath, file, { contentType: file.type || undefined, upsert: false });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from('chat-media')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  // Função para lidar com seleção de arquivo
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat || !effectiveInstanceId) {
      return;
    }
    
    // Instagram e Messenger: apenas imagem ou vídeo (sem documento/áudio)
    if (isImageVideoOnlyMode && 'full' in selectedChat) {
      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Verificar tipo de arquivo — apenas imagem ou vídeo
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        const canalLabel = chatChannel === "messenger" ? "Messenger (Facebook)" : "Instagram";
        toast.error(`No ${canalLabel} é possível enviar apenas imagens ou vídeos.`);
        return;
      }

      // Limites de tamanho por tipo (Instagram: só imagem e vídeo)
      const maxSizeImage = 5 * 1024 * 1024; // 5MB para imagens
      const maxSizeVideo = 16 * 1024 * 1024; // 16MB para vídeos
      const maxSize = isImage ? maxSizeImage : maxSizeVideo;
      
      if (file.size > maxSize) {
        const maxSizeMB = isImage ? '5MB' : '16MB';
        toast.error(`Arquivo muito grande. O limite é ${maxSizeMB}.`);
        return;
      }

      setSendingMessage(true);

      try {
        const mediaType = isImage ? 'image' : 'video';
        const instagramChat = (selectedChat as any).full;
        const effectiveChatIdFile = instagramChat.chat_id ?? (selectedChat as any).chat_id ?? (selectedChat as any).id;
        let mediaUrl: string | null = null;
        if (currentOrg?.id) {
          try {
            const chatKey = effectiveChatIdFile || instagramChat.origem_contato || 'instagram';
            mediaUrl = await uploadChatFile(file, currentOrg.id, chatKey);
          } catch (uploadError) {
            console.warn('Falha ao gerar URL do arquivo (Instagram):', uploadError);
          }
        }

        const operatorName = userProfile?.name?.trim() || 'Operador';
        const instagramFileTempId = `temp-${Date.now()}`;
        const tempMessage = {
          message_id: instagramFileTempId,
          chat_id: effectiveChatIdFile,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          user_sender: operatorName,
          sender_type: 'operator' as const,
          message_type: 'media',
          content_message: null,
          is_internal: false,
          media_type: mediaType,
          media_file_name: file.name,
          media_url: mediaUrl,
          first_viewed_at: null,
          created_at: new Date().toISOString(),
          delivery_status: 'sending' as const,
        };

        setLocalMessages(prev => [...prev, tempMessage as any]);

        const webhookResponse = await (unifiedHook as any).insertMessageAsync?.({
          chat_id: effectiveChatIdFile,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          content_message: '',
          message_type: 'media',
          is_internal: false,
          media_type: mediaType,
          media_file_name: file.name,
          media_url: mediaUrl,
          operator_name: userProfile?.name ?? undefined,
        }).catch(() => null);

        setLocalMessages(prev => prev.map((m: any) =>
          (m.message_id === instagramFileTempId || m.id === instagramFileTempId || m.delivery_status === 'sending')
            ? { ...m, delivery_status: webhookResponse?.menssage_id ? 'delivered' : 'sent_to_webhook' }
            : m
        ));

        toast.success('Arquivo enviado!');
      } catch (error) {
        console.error('Erro ao enviar arquivo Instagram:', error);
        toast.error('Erro ao enviar arquivo. Tente novamente.');
      } finally {
        setSendingMessage(false);
      }
      return;
    }
    
    // WhatsApp: comportamento original
    // Verificar se a instância selecionada é do tipo WhatsApp e tem o agente habilitado
    // Se não for válida, buscar automaticamente uma instância WhatsApp válida
    let instanceToUse = effectiveInstanceId;

    // Se a instância do chat está problemática, usar o override selecionado pelo usuário
    if (isChatInstanceProblematic) {
      if (!overrideInstanceId) {
        toast.error('Selecione uma instância ativa para enviar a mensagem.');
        return;
      }
      instanceToUse = overrideInstanceId;
    } else if (!isSelectedInstanceWhatsApp && instances && instances.length > 0 && currentOrg?.id) {
      // Buscar automaticamente uma instância WhatsApp válida
      const orgInstances = instances.filter(inst => inst.org_id === currentOrg.id);
      
      // Prioridade 1: WhatsApp com agent_enabled ligado
      const whatsappWithAgent = orgInstances.find(inst => {
        const isWhatsApp = inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS';
        return isWhatsApp && inst.agent_enabled === true;
      });
      
      if (whatsappWithAgent) {
        instanceToUse = whatsappWithAgent.id;
      } else {
        // Prioridade 2: Qualquer WhatsApp
        const anyWhatsApp = orgInstances.find(inst =>
          inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS'
        );

        if (anyWhatsApp) {
          instanceToUse = anyWhatsApp.id;
        } else {
          const selectedInstanceData = instances?.find(inst => inst.id === effectiveInstanceId);
          if (selectedInstanceData) {
            const isWhatsApp = selectedInstanceData.channel === 'whatsapp' ||
                              selectedInstanceData.integration_type === 'WHATSAPP-BAILEYS';
            if (!isWhatsApp) {
              toast.error('O envio de arquivos só está disponível para instâncias do WhatsApp.');
            } else if (!selectedInstanceData.agent_enabled) {
              toast.error('O envio de arquivos só está disponível quando o Agente Fluity está habilitado na instância WhatsApp.');
            }
          } else {
            toast.error('Nenhuma instância do WhatsApp com Agente Fluity habilitado encontrada.');
          }
          // Limpar o input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }
    }
    
    if (!instanceToUse) {
      toast.error('Nenhuma instância do WhatsApp disponível.');
      // Limpar o input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Verificar tipo de arquivo
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    const isDocument = !isImage && !isVideo && !isAudio;
    
    if (!isImage && !isVideo && !isAudio && !isDocument) {
      toast.error('Tipo de arquivo não suportado.');
      return;
    }
    
    // Limites de tamanho por tipo
    const maxSizeImage = 5 * 1024 * 1024; // 5MB para imagens
    const maxSizeOther = 16 * 1024 * 1024; // 16MB para vídeos, áudios e documentos
    const maxSize = isImage ? maxSizeImage : maxSizeOther;
    
    if (file.size > maxSize) {
      const maxSizeMB = isImage ? '5MB' : '16MB';
      toast.error(`Arquivo muito grande. O limite é ${maxSizeMB}.`);
      return;
    }
    
    setSendingMessage(true);
    
    try {
      let mediaUrl: string | null = null;
      if (currentOrg?.id) {
        try {
          const chatKey = selectedChat.id || selectedChat.phone || 'whatsapp';
          mediaUrl = await uploadChatFile(file, currentOrg.id, chatKey);
        } catch (uploadError) {
          console.warn('Falha ao gerar URL do arquivo (WhatsApp):', uploadError);
        }
      }

      const fileName = file.name;
      const fileType = isImage ? 'imagem' : isVideo ? 'vídeo' : isAudio ? 'áudio' : 'documento';
      const messageContentLocal = `📎 ${fileType}: ${fileName}`;
      const mediaTypeStr = isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : 'document';
      const fileTempId = `temp-file-${Date.now()}`;
      const fileOperatorName = userProfile?.name?.trim() || 'Operador';
      const fileTempMessage = {
        id: fileTempId,
        instance_id: instanceToUse,
        phone: selectedChat.phone,
        message_type: 'media' as const,
        bot_message: messageContentLocal,
        content_message: messageContentLocal,
        user_sender: fileOperatorName,
        sender_type: 'operator' as const,
        user_message: null,
        active: true,
        created_at: new Date().toISOString(),
        nomewpp: null,
        delivery_status: 'sending' as const,
        media_type: mediaTypeStr,
        media_file_name: fileName,
        media_url: mediaUrl ?? null,
      };
      setLocalMessages(prev => [...prev, fileTempMessage]);

      const full = (selectedChat as any).full;
      const normalizedPhoneForMessage = normalizePhone(selectedChat.phone);
      const origemContato = full?.origem_contato ?? selectedChat.phone ?? normalizedPhoneForMessage;
      // Quando a instância do chat está problemática (excluída/desconectada), usar o override selecionado
      // Caso contrário, usar a instância original do chat para manter o contexto correto no n8n
      const instanceIdForWebhook = isChatInstanceProblematic
        ? instanceToUse
        : (full?.instance_id ?? (selectedChat as any).instance_id ?? instanceToUse);
      const instanceData = instances?.find((i: any) => i.id === instanceIdForWebhook);
      const contactData = (selectedChat as any).contact ?? null;
      const now = new Date().toISOString();
      // chat_id identifica o chat em todos os canais; webhook recebe webchat_id = chat_id quando canal é webchat
      const effectiveChatId = full?.chat_id ?? (selectedChat as any).chat_id ?? selectedChat?.id;
      const chatInfo: Record<string, unknown> = {
        chat_id: effectiveChatId,
        ...(full?.channel === "webchat" && full?.chat_id ? { webchat_id: full.chat_id } : {}),
        instance_id: instanceIdForWebhook,
        origem_contato: origemContato,
        phone: selectedChat.phone,
        channel: full?.channel ?? null,
        contact_id: full?.contact_id ?? null,
        status_atd: full?.status_atd ?? null,
        atendimento_ia: full?.atendimento_ia ?? null,
        user_atribuido: full?.user_atribuido ?? null,
        momento_atribuido: full?.momento_atribuido ?? null,
        closed_at: full?.closed_at ?? null,
        user_closed_id: full?.user_closed_id ?? null,
        notes_closed: full?.notes_closed ?? null,
        created_at: full?.created_at ?? null,
        protocolo: full?.protocolo ?? null,
        resumo_atd: full?.resumo_atd ?? null,
        org_id: full?.org_id ?? currentOrg?.id ?? null,
      };
      const contactInfo: Record<string, unknown> = contactData
        ? { name: (contactData as any).name ?? null, phone: (contactData as any).phone ?? selectedChat.phone ?? null, email: (contactData as any).email ?? null }
        : { phone: selectedChat.phone, origem_contato: origemContato };

      const webhookPayload = buildOperatorMessagePayload({
        userId: user?.id ?? null,
        userEmail: (user as any)?.email ?? null,
        userName: userProfile?.name?.trim() || "Operador",
        orgId: currentOrg?.id,
        orgName: (currentOrg as any)?.name ?? null,
        instance: instanceData
          ? { id: instanceData.id, name: (instanceData as any).name, channel: (instanceData as any).channel, status: (instanceData as any).status }
          : { id: instanceIdForWebhook, name: null, channel: null, status: null },
        chat: chatInfo,
        contact: contactInfo,
        message: {
          content_message: messageContentLocal,
          message_type: "media",
          media_type: mediaTypeStr,
          media_file_name: fileName,
          media_url: mediaUrl ?? null,
          created_at: now,
        },
      });
      const webhookResponse = await sendOperatorMessageToWebhook(webhookPayload, { supabase }).catch((err) => {
        console.warn("[Chats] Webhook mensagem ao cliente (arquivo) falhou:", err);
        return null;
      });
      setLocalMessages(prev => prev.map(msg =>
        (msg.id === fileTempId || (msg as any).delivery_status === 'sending')
          ? { ...msg, delivery_status: webhookResponse?.menssage_id ? 'delivered' : 'sent_to_webhook' }
          : msg
      ));

      toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} enviado com sucesso!`);
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      toast.error('Erro ao enviar arquivo. Tente novamente.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para atualizar tags de um contato específico
  const updateContactTags = useCallback((phone: string, newTags: any[]) => {
    setContactTags(prev => ({
      ...prev,
      [phone]: newTags
    }));
  }, []);

  // Memoizar contactTags para o chat selecionado
  const selectedChatTags = useMemo(() => {
    if (!selectedChat?.phone) return [];
    return contactTags[selectedChat.phone] || [];
  }, [contactTags, selectedChat?.phone]);

  // Callback para atribuir contato (invalidate + refetch para a mensagem de atribuição aparecer no histórico)
  const handleContactAssigned = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["chat-messages"] });
    refetchMessages();
    refetchChats();
    setRefreshTrigger(prev => prev + 1);
  }, [queryClient, refetchMessages, refetchChats]);

  // Callback para carregar histórico completo
  const handleLoadFullHistory = useCallback(() => {
    setShowAllMessages(!showAllMessages);
  }, [showAllMessages]);

  // Removido: Redirecionamento para não-admins - agora membros também podem acessar a página de chats

  // Carregar tags dos contatos

  // Limpar mensagens locais quando trocar de chat e resetar filtro de 24h
  useEffect(() => {
    if (selectedChat?.id) {
      mixpanel.track('app_chats_chat_opened', { channel: ((selectedChat as any)?.channel ?? '').toLowerCase() })
    }
    setLocalMessages([]);
    setShowAllMessages(false); // Resetar para mostrar apenas últimas 24h ao trocar de chat
    setIsInternalNoteMode(false); // Desativar modo de anotação ao trocar de chat
    setNoteText(""); // Limpar texto de anotação ao trocar de chat
  }, [selectedChat?.id, setShowAllMessages]);

  // Carregar tags dos contatos quando os chats mudarem (com debounce e verificação de mudança)
  const lastFetchedPhonesRef = useRef<string>('');
  useEffect(() => {
    if (!chats.length || !currentOrg?.id || !user) return;

    // Chave do chat: phone ou origem_contato (para incluir todos os clientes na lista de tags)
    const getChatPhone = (chat: any) => chat.phone || (chat as any).origem_contato || "";
    const phoneNumbers = chats
      .filter(chat => getChatPhone(chat))
      .map(chat => getChatPhone(chat))
      .sort()
      .join(',');
    
    // Só buscar se os números realmente mudaram
    if (lastFetchedPhonesRef.current === phoneNumbers) {
      return;
    }

    // Debounce para evitar múltiplas chamadas rápidas
    const timeoutId = setTimeout(async () => {
      // Verificar novamente após o debounce
      if (lastFetchedPhonesRef.current === phoneNumbers) {
        return;
      }
      
      lastFetchedPhonesRef.current = phoneNumbers;

      try {
        const phoneArray = chats
          .filter(chat => getChatPhone(chat))
          .map(chat => getChatPhone(chat));

        const { data: contactsWithTags } = await supabase
          .rpc('get_contacts_with_tags', {
            phone_numbers: phoneArray,
            organization_id: currentOrg.id
          });

        // Agrupar tags por telefone
        const tagsMap: Record<string, any[]> = {};
        
        contactsWithTags?.forEach((row: any) => {
          if (!tagsMap[row.phone]) {
            tagsMap[row.phone] = [];
          }
          
          // Adicionar tag se existir
          if (row.tag_id) {
            const tag = {
              id: row.tag_id,
              name: row.tag_name,
              color: row.tag_color,
              description: row.tag_description
            };
            
            // Verificar se a tag já foi adicionada (evitar duplicatas)
            const tagExists = tagsMap[row.phone].some((existingTag: any) => existingTag.id === tag.id);
            if (!tagExists) {
              tagsMap[row.phone].push(tag);
            }
          }
        });

        setContactTags(tagsMap);
      } catch (error) {
        console.error('Erro ao buscar tags dos contatos:', error);
      }
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
  }, [chats.length, currentOrg?.id, user?.id]); // Usar chats.length em vez de chats inteiro

  // Resetar limite de mensagens quando o chat muda (otimizado: mostrar mais mensagens inicialmente)
  useEffect(() => {
    setMessagesLimit(30); // Aumentar para 30 mensagens iniciais para melhor UX
    // Habilitar auto-scroll quando mudar de chat (primeira abertura)
    shouldAutoScrollRef.current = true;
    isUserScrollingRef.current = false;
    // O scroll será feito pelo useEffect que observa localMessages e aguarda carregamento de imagens
  }, [selectedChat?.id]);

  // Ativar loading ao trocar de chat + safety timeout para não ficar preso
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const currentId = selectedChat?.id ?? null;
    if (currentId && currentId !== prevSelectedChatIdRef.current) {
      setIsLoadingMessages(true);
      chatLoadStartRef.current = performance.now();
      prevSelectedChatIdRef.current = currentId;
      // Safety: never spin longer than 5s regardless of what happens
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = setTimeout(() => setIsLoadingMessages(false), 5000);
    }
    if (!currentId) {
      prevSelectedChatIdRef.current = null;
      chatLoadStartRef.current = null;
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    }
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [selectedChat?.id]);

  // Sincronizar mensagens locais com as do hook (selectedChatMessages) - otimizado
  useEffect(() => {
    if (!selectedChatMessages) {
      setLocalMessages([]);
      return;
    }

    if (!Array.isArray(selectedChatMessages)) {
      setLocalMessages([]);
      return;
    }

    if (selectedChatMessages.length === 0) {
      setLocalMessages([]);
      setIsLoadingMessages(false);
      return;
    }

    // Identificar a PRIMEIRA mensagem não lida (mais antiga) e contar todas as não lidas
    // selectedChatMessages já está ordenado ascending (do mais antigo ao mais recente)
    let firstUnreadId: string | null = null;
    let unreadCount = 0;
    const isUuidStr = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    for (const msg of selectedChatMessages) {
      const userSender = String((msg as any).user_sender || '').trim();
      const origemContato = String((msg as any).origem_contato || '').trim();
      const senderType = (msg as any).sender_type;
      const msgAgentId = String((msg as any).agent_id || '').trim();
      // agent_id preenchido ou UUID no user_sender = operador/sistema da plataforma, nunca contato
      const isSenderUuid = isUuidStr(userSender);
      const isClientMsg = !isUuidStr(msgAgentId) && !isSenderUuid && (
        senderType === 'contact' ||
        !userSender || userSender === 'Cliente' ||
        (origemContato !== '' && userSender === origemContato) ||
        msg.message_type === 'user' || msg.message_type === 'messages.upsert'
      );
      if (isClientMsg && !(msg as any).first_viewed_at) {
        if (!firstUnreadId) firstUnreadId = msg.id;
        unreadCount++;
      }
    }
    setLastUnreadMessageId(firstUnreadId);
    setLocalUnreadCount(unreadCount);

    // Aplicar limite de mensagens para exibição (mostrar as últimas N mensagens)
    const limitedMessages = selectedChatMessages.slice(-messagesLimit);

    // Instagram: user_sender = origem do contato (ou "Cliente") = mensagem do cliente (esquerda). Operador/Bot/Sistema ou nome do operador = direita.
    let messagesToSet = limitedMessages;
    if (isInstagramMode && limitedMessages.length > 0 && 'content_message' in limitedMessages[0]) {
      const chatPhone = (selectedChat as any)?.phone || (selectedChat as any)?.full?.origem_contato;
      const origemContato = (limitedMessages[0] as any)?.origem_contato || chatPhone || '';
      const contactDisplayName = (selectedChat as any)?.contactName ?? (selectedChat as any)?.full?.contact?.name ?? (selectedChat as any)?.name ?? null;
      const isUuidNorm = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
      messagesToSet = limitedMessages.map((msg: any) => {
        const userSender = String(msg.user_sender || '').trim();
        const msgOrigem = String((msg.origem_contato ?? origemContato) ?? '').trim();
        const msgAgentId = String(msg.agent_id || '').trim();
        // agent_id preenchido = usuário da plataforma enviou a mensagem. UUID no user_sender = idem.
        const isFromContact = !isUuidNorm(msgAgentId) && !isUuidNorm(userSender) && (!userSender || userSender === 'Cliente' || userSender === msgOrigem || userSender === String(origemContato || '').trim());
        const isOperatorOrBot = !isFromContact;
        const sender_type: 'contact' | 'operator' | 'bot' | 'system' = isOperatorOrBot
          ? (userSender === 'Sistema' ? 'system' : userSender === 'Bot' ? 'bot' : 'operator')
          : 'contact';
        return {
          ...msg,
          content: msg.content_message ?? '',
          content_message: msg.content_message ?? '',
          user_message: msg.content_message ?? msg.user_message ?? '',
          sender_type,
          user_sender: msg.user_sender,
          phone: chatPhone || msg.origem_contato,
          instance_id: msg.instance_id || effectiveInstanceId,
          is_internal: msg.is_internal === true,
          contactName: contactDisplayName,
          nomewpp: contactDisplayName,
        };
      });
    }

    setLocalMessages(prev => {
      // Separar mensagens temporárias (criadas localmente, aguardando confirmação)
      const tempMessages = (prev || []).filter((m: any) => {
        const id = m.id ?? m.message_id;
        return id && String(id).startsWith('temp-');
      });

      if (tempMessages.length === 0) {
        return messagesToSet;
      }

      // Controla quais temps já foram "reivindicadas" por uma mensagem do servidor
      const matchedTempKeys = new Set<string>();

      const mergedServer = messagesToSet.map((serverMsg: any) => {
        const serverContent = (serverMsg.content_message ?? serverMsg.bot_message ?? '').trim();
        const serverSender = String(serverMsg.user_sender ?? '').trim();
        const serverTime = new Date(serverMsg.created_at || 0).getTime();

        // Só tentar casar com temps se for mensagem de operador/bot
        const serverIsOperator =
          serverMsg.sender_type === 'operator' ||
          serverMsg.sender_type === 'bot' ||
          serverMsg.message_type === 'bot' ||
          (serverSender && serverSender !== 'Cliente');

        if (!serverIsOperator) return serverMsg;

        const matchingTemp = tempMessages.find((t: any) => {
          const tempKey = String(t.id ?? t.message_id ?? '');
          if (matchedTempKeys.has(tempKey)) return false;

          const tempContent = (t.content_message ?? t.bot_message ?? '').trim();
          if (tempContent !== serverContent) return false;

          const tempTime = new Date(t.created_at || 0).getTime();
          // Server message deve ter sido salvo até 45s depois da temp (permite latência)
          if (serverTime < tempTime - 3000 || serverTime > tempTime + 45000) return false;

          // Se temp tem user_sender definido, validar que bate com o servidor
          const tempSender = String(t.user_sender ?? '').trim();
          if (tempSender && serverSender && tempSender !== serverSender) return false;

          return true;
        });

        if (matchingTemp) {
          matchedTempKeys.add(String(matchingTemp.id ?? matchingTemp.message_id ?? ''));
          // Se temp ainda está 'sending', a chegada do servidor confirma ao menos 'sent_to_webhook'
          const inheritedStatus = matchingTemp.delivery_status === 'sending'
            ? 'sent_to_webhook'
            : matchingTemp.delivery_status;
          return { ...serverMsg, delivery_status: inheritedStatus };
        }
        return serverMsg;
      });

      // Manter somente temps que não foram casadas com nenhuma mensagem do servidor
      const stillPending = tempMessages.filter((t: any) => {
        const tempKey = String(t.id ?? t.message_id ?? '');
        return !matchedTempKeys.has(tempKey);
      });

      const combined = [...mergedServer, ...stillPending];
      combined.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
      return combined;
    });
    setIsLoadingMessages(false);
    if (chatLoadStartRef.current !== null) {
      const ms = performance.now() - chatLoadStartRef.current;
      const label = (selectedChat as any)?.contactName ?? (selectedChat as any)?.name ?? selectedChat?.phone ?? selectedChat?.id ?? '—';
      console.log(`[Chat] ⏱ "${label}" — mensagens carregadas em ${ms.toFixed(0)}ms`);
      chatLoadStartRef.current = null;
    }
  }, [selectedChatMessages, messagesLimit, selectedChat?.id, isInstagramMode, selectedInstance]);

  // Agrupar mensagens por data para exibir separadores (Hoje, Ontem, 15 de março de 2026, etc.)
  const messagesGroupedByDate = useMemo(() => {
    const list = localMessages && Array.isArray(localMessages) ? localMessages : [];
    const toGroup = closedInfo
      ? list.filter((msg: any) => {
          const botMsg = String(msg.bot_message ?? '');
          const contentMsg = String(msg.content_message ?? msg.content ?? '');
          return !(
            (botMsg && botMsg.includes("encerrado por")) ||
            /^Chat encerrado/i.test(contentMsg.trim())
          );
        })
      : list;
    if (toGroup.length === 0) return [];

    const groups: { dateLabel: string; dateKey: string; messages: typeof toGroup }[] = [];
    let currentDateKey = "";
    let currentGroup: typeof toGroup = [];

    const getDateKey = (createdAt: string | undefined) => {
      if (!createdAt) return "";
      const d = new Date(createdAt);
      return format(d, "yyyy-MM-dd");
    };
    const getDateLabel = (createdAt: string | undefined) => {
      if (!createdAt) return "";
      const d = new Date(createdAt);
      if (isToday(d)) return "Hoje";
      if (isYesterday(d)) return "Ontem";
      return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
    };
    const getCreatedAt = (msg: any) => msg?.created_at ?? (msg as any)?.created_at;

    for (const msg of toGroup) {
      const createdAt = getCreatedAt(msg);
      const dateKey = getDateKey(createdAt);
      if (dateKey !== currentDateKey) {
        if (currentGroup.length > 0) {
          groups.push({
            dateKey: currentDateKey,
            dateLabel: getDateLabel(getCreatedAt(currentGroup[0])),
            messages: [...currentGroup],
          });
        }
        currentDateKey = dateKey;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    }
    if (currentGroup.length > 0) {
      groups.push({
        dateKey: currentDateKey,
        dateLabel: getDateLabel(getCreatedAt(currentGroup[0])),
        messages: [...currentGroup],
      });
    }
    return groups;
  }, [localMessages, closedInfo]);

  // Scroll para o final após as mensagens serem renderizadas e imagens carregadas
  // APENAS quando o chat é aberto pela primeira vez ou quando novas mensagens chegam
  useEffect(() => {
    if (!localMessages || localMessages.length === 0) return;

    // Ignorar atualizações de cache (mesmo chat, mesma quantidade de mensagens) — ex.: markAsViewed
    const currentChatId = selectedChat?.id ?? null;
    const currentCount = localMessages.length;
    const lastState = lastScrollChatStateRef.current;
    const isChatChange = currentChatId !== lastState.chatId;
    const isNewMessages = currentChatId === lastState.chatId && currentCount > lastState.count;
    if (!isChatChange && !isNewMessages) return;
    lastScrollChatStateRef.current = { chatId: currentChatId, count: currentCount };

    // Não fazer scroll se o usuário está rolando manualmente
    if (isUserScrollingRef.current) {
      return;
    }

    // Não fazer scroll se não deve fazer auto-scroll
    if (!shouldAutoScrollRef.current) {
      return;
    }
    
    // Função para fazer scroll para o final
    const scrollToBottom = () => {
      const container = messagesContainerRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      } else if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
      }
    };

    // Função para scroll: vai para o marcador de não lidas se existir, senão vai para o final
    const scrollToUnreadOrBottom = () => {
      if (unreadMarkerRef.current) {
        unreadMarkerRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else {
        scrollToBottom();
      }
    };

    // Aguardar que todas as imagens sejam carregadas antes de fazer scroll
    const checkImagesLoaded = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      // Buscar todas as imagens dentro do container
      const images = container.querySelectorAll('img');
      const videos = container.querySelectorAll('video');
      
      let loadedCount = 0;
      const totalMedia = images.length + videos.length;

      // Se não houver mídia, fazer scroll imediatamente
      if (totalMedia === 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToUnreadOrBottom();
          });
        });
        return;
      }

      // Função para verificar se todas as mídias foram carregadas
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalMedia) {
          // Aguardar um frame adicional para garantir que o layout está atualizado
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollToUnreadOrBottom();
            });
          });
        }
      };

      // Adicionar listeners para imagens
      images.forEach((img) => {
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete, { once: true });
          img.addEventListener('error', checkComplete, { once: true });
        }
      });

      // Adicionar listeners para vídeos
      videos.forEach((video) => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          checkComplete();
        } else {
          video.addEventListener('loadeddata', checkComplete, { once: true });
          video.addEventListener('error', checkComplete, { once: true });
        }
      });

      // Timeout de segurança: fazer scroll após 1 segundo mesmo se algumas imagens não carregarem
      setTimeout(() => {
        scrollToUnreadOrBottom();
      }, 1000);
    };

    // Aguardar que o DOM seja atualizado antes de verificar imagens
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkImagesLoaded();
      });
    });
  }, [localMessages, selectedChat?.id]); // Adicionar selectedChat?.id para garantir scroll quando mudar de chat

  // Marcar mensagens como lidas quando o usuário visualiza a conversa
  useEffect(() => {
    if (!selectedChat || !user?.id || !selectedChatMessages || selectedChatMessages.length === 0) return;
    
    const markMessagesAsRead = async () => {
      // Identificar mensagens não lidas do usuário (não do bot)
      const unreadMessages = selectedChatMessages.filter(msg => {
        const readBy = (msg as any).read_by || [];
        const isRead = Array.isArray(readBy) && readBy.includes(user.id);
        return (msg.message_type === 'user' || msg.message_type === 'messages.upsert') && !isRead;
      });
      
      const unreadMessageIds = unreadMessages.map(msg => msg.id);
      
      if (unreadMessageIds.length === 0) return;
      
      try {
        // Otimização: Marcar todas de uma vez usando RPC ou bulk update se possível
        // Tentar usar RPC primeiro, com fallback se não existir
        const { error } = await supabase
          .rpc('mark_messages_as_read_bulk', {
            message_ids: unreadMessageIds,
            user_id: user.id
          });
        
        // Se houver erro (incluindo 404 se RPC não existir), usar fallback
        // Não logar erro 404 pois é esperado se o RPC não existir
        if (error) {
          // Verificar se é erro 404 (RPC não existe) ou outro erro
          const is404Error = error.code === 'PGRST116' || 
                            error.message?.includes('404') || 
                            error.message?.includes('not found') ||
                            error.message?.includes('does not exist');
          
          // RPC não existe ou outro erro — fallback ignorado pois chat_messages (legada) não existe
        }
        
        // Atualizar estado local de uma vez
        setLocalMessages(prev => prev.map(msg => {
          if (unreadMessageIds.includes(msg.id)) {
            const readBy = ((msg as any).read_by || []) as string[];
            return {
              ...msg,
              read_by: readBy.includes(user.id) ? readBy : [...readBy, user.id],
              first_viewed_at: msg.first_viewed_at || new Date().toISOString()
            };
          }
          return msg;
        }));

        // ATUALIZAÇÃO CRÍTICA: Limpar contador no cache global imediatamente
        // Instagram isolated path - Não fazer normalizePhone em Instagram
        if (!isInstagramMode) {
          queryClient.setQueryData(
            ["chats", currentOrg.id, user.id],
            (oldData: any[] | undefined) => {
              if (!oldData || !Array.isArray(oldData)) return oldData;
              return oldData.map(chat => {
                if (normalizePhone(chat.phone) === normalizePhone(selectedChat.phone)) {
                  return { ...chat, unreadCount: 0 };
                }
                return chat;
              });
            }
          );
        }
      } catch (error) {
        console.error('Erro ao marcar mensagens como lidas:', error);
      }
    };
    
    // Marcar como lidas imediatamente quando o chat é visualizado
    // Não usar delay muito longo para melhor UX
    const timer = setTimeout(() => {
      markMessagesAsRead();
    }, 500);
    
    return () => clearTimeout(timer);
  // selectedChatMessages intentionally excluded: runs once per chat open, not on every new message
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id, user?.id, refetchChats]);

  // Handler para tecla ESC - deselecionar chat e fechar menu de macros
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedChat) {
        setSelectedChat(null);
        setShowMobileConversation(false);
      }
      // Fechar menu de macros com ESC
      if (event.key === 'Escape' && showMacroMenu) {
        setShowMacroMenu(false);
        setMacroSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedChat, setSelectedChat, showMacroMenu]);

  // Fechar menu de macros ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMacroMenu && messageInputRef.current && !messageInputRef.current.contains(event.target as Node)) {
        // Verificar se o clique não foi no menu de macros
        const macroMenu = document.querySelector('[class*="fixed z-[100]"]');
        if (macroMenu && !macroMenu.contains(event.target as Node)) {
          setShowMacroMenu(false);
          setMacroSearchQuery('');
        }
      }
    };

    if (showMacroMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMacroMenu]);

  // Infinite scroll - carregar mais mensagens quando rolar para o topo
  useEffect(() => {
    if (!messagesContainerRef.current || !selectedChatMessages) return;

    // Encontrar o viewport do ScrollArea
    const container = messagesContainerRef.current.closest('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!container) return;

    const handleScroll = () => {
      // Verificar se está próximo do topo (dentro de 200px)
      if (container.scrollTop < 200 && !isLoadingMoreMessages) {
        // Verificar se há mais mensagens para carregar
        if (messagesLimit < selectedChatMessages.length) {
          setIsLoadingMoreMessages(true);
          
          // Salvar posição atual do scroll
          const scrollPosition = container.scrollHeight - container.scrollTop;
          
          // Aumentar limite em 10 mensagens
          const newLimit = Math.min(messagesLimit + 10, selectedChatMessages.length);
          setMessagesLimit(newLimit);
          
          // Restaurar posição do scroll após renderizar
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - scrollPosition;
            setIsLoadingMoreMessages(false);
          }, 150);
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messagesLimit, selectedChatMessages, isLoadingMoreMessages]);

  // Auto-scroll para o final das mensagens quando novas mensagens chegam (apenas se já estiver no final)
  useEffect(() => {
    if (!messagesEndRef.current || lastUnreadMessageId !== null) return;
    
    // Não fazer scroll se o usuário está rolando manualmente
    if (isUserScrollingRef.current) {
      return;
    }
    
    const container = messagesContainerRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!container) return;
    
    // Verificar se o usuário está próximo do final (dentro de 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom) {
      // Scroll suave apenas se já estiver no final (para novas mensagens)
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [localMessages.length, lastUnreadMessageId]);

  // Detectar quando o usuário está rolando manualmente
  useEffect(() => {
    const container = messagesContainerRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const isAtBottom = scrollHeight - currentScrollTop - clientHeight < 50;
      
      // Se o usuário está rolando para cima (scrollTop diminuiu), marcar como scroll manual
      if (currentScrollTop < lastScrollTopRef.current) {
        isUserScrollingRef.current = true;
        shouldAutoScrollRef.current = false;
      }
      
      // Se o usuário chegou ao final, reabilitar auto-scroll
      if (isAtBottom) {
        isUserScrollingRef.current = false;
        shouldAutoScrollRef.current = true;
      }
      
      lastScrollTopRef.current = currentScrollTop;
      
      // Limpar timeout anterior
      clearTimeout(scrollTimeout);
      
      // Após 2 segundos sem scroll, considerar que o usuário parou de rolar
      scrollTimeout = setTimeout(() => {
        // Se estiver no final, reabilitar auto-scroll
        if (isAtBottom) {
          isUserScrollingRef.current = false;
          shouldAutoScrollRef.current = true;
        }
      }, 2000);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [selectedChat?.id]);

  // As mensagens agora são gerenciadas centralmente no hook useChats via React Query
  // com subscriptions em tempo real configuradas lá.

  // Função para salvar mensagem interna (anotação)
  const saveInternalNote = async () => {
    if (!noteText.trim() || !selectedChat || !effectiveInstanceId || sendingMessage) {
      return;
    }
    mixpanel.track('app_chats_internal_note_sent')

    setSendingMessage(true);
    const messageContent = noteText.trim();
    setNoteText("");
    setIsInternalNoteMode(false); // Desativar modo de anotação após salvar

    try {
      // Instagram isolated path - Usar tabela separada (sem nome do usuário dentro da nota)
      if (isInstagramMode && 'full' in selectedChat) {
        const instagramChat = (selectedChat as any).full;
        unifiedHook.insertMessage({
          chat_id: instagramChat.chat_id,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          content_message: `📝 NOTA INTERNA:\n${messageContent}`,
          message_type: 'internal',
          is_internal: true,
          operator_name: userProfile?.name ?? undefined,
        });
        toast.success('Anotação interna salva com sucesso!');
      } else {
        // WhatsApp: persistir na tabela unificada chat_messages_instagram quando o chat tiver chat_id
        const full = (selectedChat as any).full;
        const chatId = full?.chat_id ?? (selectedChat as any).chat_id;
        const instanceId = full?.instance_id ?? selectedChat?.instance_id ?? effectiveInstanceId;
        const origemContato = full?.origem_contato ?? selectedChat?.phone;
        if (chatId && instanceId) {
          unifiedHook.insertMessage({
            chat_id: chatId,
            instance_id: instanceId,
            origem_contato: origemContato ?? selectedChat.phone ?? '',
            content_message: `📝 NOTA INTERNA:\n${messageContent}`,
            message_type: 'internal',
            is_internal: true,
            operator_name: userProfile?.name ?? undefined,
          });
          if (refetchMessages) refetchMessages();
          toast.success('Anotação interna salva com sucesso!');
        } else {
          // Tabela chat_messages removida; anotação só persiste em chat_messages_instagram (path acima)
          toast.info('Use o canal unificado (Instagram/Webchat) para anotações internas.');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar anotação interna:', error);
      toast.error('Erro ao salvar anotação. Tente novamente.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar mensagem via Evolution API
  const autoAssignIfUnassigned = useCallback(async (chatData: any) => {
    if (!user?.id || !currentOrg?.id || chatData?.user_atribuido) return;
    const chatId = chatData?.chat_id ?? null;
    const instanceId = chatData?.instance_id ?? null;
    const origemContato = chatData?.origem_contato ?? null;
    const now = new Date().toISOString();
    if (!instanceId) return;
    const conversations = chatSupabase.from('conversations');
    try {
      if (chatId) {
        await conversations
          .update({ user_atribuido: user.id, momento_atribuido: now })
          .eq('chat_id', chatId)
          .eq('org_id', currentOrg.id);
      } else if (origemContato) {
        await conversations
          .update({ user_atribuido: user.id, momento_atribuido: now })
          .eq('instance_id', instanceId)
          .eq('origem_contato', origemContato)
          .eq('org_id', currentOrg.id);
      }
      handleContactAssigned();
    } catch (e) {
      console.warn('[Chats] autoAssignIfUnassigned error:', e);
    }
  }, [user?.id, currentOrg?.id, supabase, handleContactAssigned]);

  const sendMessage = async () => {
    // Se estiver no modo de anotação interna, salvar como anotação
    if (isInternalNoteMode) {
      if (!noteText.trim() || !selectedChat || !effectiveInstanceId || sendingMessage) {
        return;
      }
      await saveInternalNote();
      return;
    }

    if (!messageText.trim() || !selectedChat || !effectiveInstanceId || sendingMessage) {
      return;
    }
    mixpanel.track('app_chats_message_sent', { channel: chatChannel })

    // Instagram isolated path - Envio para Instagram
    if (isInstagramMode && 'full' in selectedChat) {
      setSendingMessage(true);
      const messageContent = messageText.trim();
      setMessageText("");
      const instagramTempId = `temp-${Date.now()}`;

      try {
        const instagramChat = (selectedChat as any).full;
        const effectiveChatId = instagramChat.chat_id ?? (selectedChat as any).chat_id ?? (selectedChat as any).id;
        const operatorName = userProfile?.name?.trim() || 'Operador';
        const tempMessage = {
          message_id: instagramTempId,
          chat_id: effectiveChatId,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          user_sender: operatorName,
          sender_type: 'operator' as const,
          message_type: 'text',
          content_message: messageContent,
          content: messageContent,
          is_internal: false,
          media_type: null,
          media_file_name: null,

          media_url: null,
          first_viewed_at: null,
          created_at: new Date().toISOString(),
          delivery_status: 'sending' as const,
        };

        setLocalMessages(prev => [...prev, tempMessage as any]);

        const webhookResponse = await (unifiedHook as any).insertMessageAsync?.({
          chat_id: effectiveChatId,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          content_message: messageContent,
          message_type: 'text',
          is_internal: false,
          operator_name: userProfile?.name ?? undefined,
        }).catch(() => null);

        // Chamar webhook quando operador responde (mesmo payload do WhatsApp)
        const instanceData = instances?.find((i: any) => i.id === instagramChat.instance_id);
        const contactData = (selectedChat as any).contact ?? null;
        const now = new Date().toISOString();
        const operatorPayload = buildOperatorMessagePayload({
          userId: user?.id ?? null,
          userEmail: (user as any)?.email ?? null,
          userName: operatorName,
          orgId: currentOrg?.id,
          orgName: (currentOrg as any).name ?? null,
          instance: instanceData
            ? { id: instanceData.id, name: (instanceData as any).name, channel: (instanceData as any).channel, status: (instanceData as any).status }
            : { id: instagramChat.instance_id, name: null, channel: 'instagram', status: null },
          chat: {
            chat_id: effectiveChatId,
            ...((instagramChat as any)?.channel === "webchat" && (instagramChat as any)?.chat_id ? { webchat_id: (instagramChat as any).chat_id } : {}),
            instance_id: instagramChat.instance_id,
            origem_contato: instagramChat.origem_contato,
            phone: instagramChat.origem_contato ?? null,
            channel: instagramChat.channel ?? null,
            contact_id: instagramChat.contact_id ?? null,
            status_atd: instagramChat.status_atd ?? null,
            atendimento_ia: instagramChat.atendimento_ia ?? null,
            user_atribuido: instagramChat.user_atribuido ?? null,
            momento_atribuido: instagramChat.momento_atribuido ?? null,
            closed_at: instagramChat.closed_at ?? null,
            user_closed_id: instagramChat.user_closed_id ?? null,
            notes_closed: instagramChat.notes_closed ?? null,
            created_at: instagramChat.created_at ?? null,
            protocolo: instagramChat.protocolo ?? null,
            resumo_atd: instagramChat.resumo_atd ?? null,
            org_id: instagramChat.org_id ?? currentOrg?.id ?? null,
          },
          contact: contactData
            ? { name: (contactData as any).name ?? null, phone: (contactData as any).phone ?? instagramChat.origem_contato ?? null, email: (contactData as any).email ?? null }
            : { phone: instagramChat.origem_contato ?? null, origem_contato: instagramChat.origem_contato ?? null },
          message: {
            content_message: messageContent,
            message_type: 'text',
            media_type: null,
            media_file_name: null,
  
            media_url: null,
            created_at: now,
          },
        });
        const webhookResponseOperador = await sendOperatorMessageToWebhook(operatorPayload, { supabase }).catch((err) => {
          console.warn("[Chats] Webhook (operador responde) falhou:", err);
          return null;
        });

        await autoAssignIfUnassigned({
          chat_id: effectiveChatId,
          instance_id: instagramChat.instance_id,
          origem_contato: instagramChat.origem_contato,
          user_atribuido: instagramChat.user_atribuido ?? null,
        });

        setLocalMessages(prev => prev.map((m: any) =>
          (m.message_id === instagramTempId || m.id === instagramTempId || m.delivery_status === 'sending')
            ? { ...m, delivery_status: webhookResponseOperador?.menssage_id ? 'delivered' : 'sent_to_webhook' }
            : m
        ));

        toast.success('Mensagem enviada!');
      } catch (error) {
        console.error('Erro ao enviar mensagem Instagram:', error);
        toast.error('Erro ao enviar mensagem. Tente novamente.');
      } finally {
        setSendingMessage(false);
      }
      return;
    }

    // WhatsApp: comportamento original
    // Verificar se a instância selecionada é do tipo WhatsApp e tem o agente habilitado
    // Se não for válida, buscar automaticamente uma instância WhatsApp válida
    let instanceToUse = effectiveInstanceId;

    // Se a instância do chat está problemática, usar o override selecionado pelo usuário
    if (isChatInstanceProblematic) {
      if (!overrideInstanceId) {
        toast.error('Selecione uma instância ativa para enviar a mensagem.');
        return;
      }
      instanceToUse = overrideInstanceId;
    } else if (!isSelectedInstanceWhatsApp && instances && instances.length > 0 && currentOrg?.id) {
      // Buscar automaticamente uma instância WhatsApp válida
      const orgInstances = instances.filter(inst => inst.org_id === currentOrg.id);
      
      // Prioridade 1: WhatsApp com agent_enabled ligado
      const whatsappWithAgent = orgInstances.find(inst => {
        const isWhatsApp = inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS';
        return isWhatsApp && inst.agent_enabled === true;
      });
      
      if (whatsappWithAgent) {
        instanceToUse = whatsappWithAgent.id;
      } else {
        // Prioridade 2: Qualquer WhatsApp
        const anyWhatsApp = orgInstances.find(inst =>
          inst.channel === 'whatsapp' || inst.integration_type === 'WHATSAPP-BAILEYS'
        );

        if (anyWhatsApp) {
          instanceToUse = anyWhatsApp.id;
        } else {
          const selectedInstanceData = instances?.find(inst => inst.id === effectiveInstanceId);
          if (selectedInstanceData) {
            const isWhatsApp = selectedInstanceData.channel === 'whatsapp' ||
                              selectedInstanceData.integration_type === 'WHATSAPP-BAILEYS';
            if (!isWhatsApp) {
              toast.error('O envio de mensagens só está disponível para instâncias do WhatsApp.');
            } else if (!selectedInstanceData.agent_enabled) {
              toast.error('O envio de mensagens só está disponível quando o Agente Fluity está habilitado na instância WhatsApp.');
            }
          } else {
            toast.error('Nenhuma instância do WhatsApp com Agente Fluity habilitado encontrada.');
          }
          return;
        }
      }
    }
    
    if (!instanceToUse) {
      toast.error('Nenhuma instância do WhatsApp disponível.');
      return;
    }

    setSendingMessage(true);
    let messageContent = messageText.trim();
    
    // Verificar se é uma referência a macro (começa com "\")
    if (messageContent.startsWith('\\')) {
      const macroQuery = messageContent.slice(1).toLowerCase().trim();
      
      if (macroQuery) {
        // Buscar macro por título (case-insensitive)
        const foundMacro = macros.find((macro: Macro) => 
          macro.title.toLowerCase() === macroQuery ||
          macro.title.toLowerCase().includes(macroQuery) ||
          macro.content.toLowerCase().includes(macroQuery)
        );
        
        if (foundMacro) {
          // Usar o conteúdo da macro encontrada
          messageContent = foundMacro.content;
          toast.success(`Macro "${foundMacro.title}" aplicada!`);
        } else {
          // Macro não encontrada
          toast.error(`Macro "\\${macroQuery}" não encontrada. Verifique o nome ou crie uma nova macro.`);
          setSendingMessage(false);
          return;
        }
      } else {
        // Apenas "\" sem nome - não enviar
        toast.error('Digite o nome da macro após o "\\" ou selecione uma macro do menu.');
        setSendingMessage(false);
        return;
      }
    }
    
    // Substituir variáveis dinâmicas nas macros
    if (messageContent.includes('{{')) {
      try {
        const { data: contact } = await supabase
          .from('contacts')
          .select('name, email, phone')
          .eq('org_id', currentOrg?.id)
          .eq('phone', normalizePhone(selectedChat.phone))
          .maybeSingle();

        const variables = {
          nome_cliente: contact?.name || selectedChat.phone,
          telefone: selectedChat.phone,
          email: contact?.email || '',
          protocolo: '',
          operador: userProfile?.name || 'Atendente',
        };

        messageContent = replaceMacroVariables(messageContent, variables);
      } catch (varErr) {
        console.warn('[Chats] Erro ao substituir variáveis da macro:', varErr);
      }
    }
    
    // Fechar menu de macros se estiver aberto
    if (showMacroMenu) {
      setShowMacroMenu(false);
      setMacroSearchQuery('');
    }
    
    setMessageText("");

    const tempId = `temp-${Date.now()}`;
    const operatorName = userProfile?.name?.trim() || 'Operador';
    const tempMessage = {
      id: tempId,
      instance_id: instanceToUse,
      phone: selectedChat.phone,
      message_type: 'bot',
      bot_message: messageContent,
      content_message: messageContent,
      user_sender: operatorName,
      sender_type: 'operator' as const,
      user_message: null,
      active: true,
      created_at: new Date().toISOString(),
      nomewpp: null,
      delivery_status: 'sending' as const,
    };

    setLocalMessages(prev => [...prev, tempMessage]);

    setTimeout(async () => {
      try {
        const normalizedPhoneForMessage = normalizePhone(selectedChat.phone);
        const full = (selectedChat as any).full;
        // chat_id identifica o chat em todos os canais
        const effectiveChatId = full?.chat_id ?? (selectedChat as any).chat_id ?? selectedChat?.id;
        const origemContato = full?.origem_contato ?? selectedChat.phone ?? normalizedPhoneForMessage;
        // Webhook deve receber a instância do chat que está sendo respondido (não a instância selecionada no dropdown)
        const instanceIdForWebhook = full?.instance_id ?? (selectedChat as any).instance_id ?? instanceToUse;
        const instanceData = instances?.find((i: any) => i.id === instanceIdForWebhook);
        const contactData = (selectedChat as any).contact ?? null;
        const now = new Date().toISOString();

        const chatInfo: Record<string, unknown> = {
          chat_id: effectiveChatId,
          ...(full?.channel === "webchat" && full?.chat_id ? { webchat_id: full.chat_id } : {}),
          instance_id: instanceIdForWebhook,
          origem_contato: origemContato,
          phone: selectedChat.phone,
          channel: full?.channel ?? null,
          contact_id: full?.contact_id ?? null,
          status_atd: full?.status_atd ?? null,
          atendimento_ia: full?.atendimento_ia ?? null,
          user_atribuido: full?.user_atribuido ?? null,
          momento_atribuido: full?.momento_atribuido ?? null,
          closed_at: full?.closed_at ?? null,
          user_closed_id: full?.user_closed_id ?? null,
          notes_closed: full?.notes_closed ?? null,
          created_at: full?.created_at ?? null,
          protocolo: full?.protocolo ?? null,
          resumo_atd: full?.resumo_atd ?? null,
          org_id: full?.org_id ?? currentOrg?.id ?? null,
        };
        const contactInfo: Record<string, unknown> = contactData
          ? { name: (contactData as any).name ?? null, phone: (contactData as any).phone ?? selectedChat.phone ?? null, email: (contactData as any).email ?? null }
          : { phone: selectedChat.phone, origem_contato: origemContato };

        const webhookPayload = buildOperatorMessagePayload({
          userId: user?.id ?? null,
          userEmail: (user as any)?.email ?? null,
          userName: userProfile?.name?.trim() || "Operador",
          orgId: currentOrg?.id,
          orgName: (currentOrg as any).name ?? null,
          instance: instanceData
            ? { id: instanceData.id, name: (instanceData as any).name, channel: (instanceData as any).channel, status: (instanceData as any).status }
            : { id: instanceIdForWebhook, name: null, channel: null, status: null },
          chat: chatInfo,
          contact: contactInfo,
          message: {
            content_message: messageContent,
            message_type: "text",
            media_type: null,
            media_file_name: null,
  
            media_url: null,
            created_at: now,
          },
        });
        const webhookResponse = await sendOperatorMessageToWebhook(webhookPayload, { supabase }).catch((err) => {
          console.warn("[Chats] Webhook mensagem ao cliente falhou:", err);
          return null;
        });

        await autoAssignIfUnassigned({
          chat_id: effectiveChatId,
          instance_id: instanceIdForWebhook,
          origem_contato: origemContato,
          user_atribuido: full?.user_atribuido ?? null,
        });

        setLocalMessages(prev => prev.map(msg =>
          (msg.id === tempId || (msg as any).delivery_status === 'sending')
            ? { ...msg, delivery_status: webhookResponse?.menssage_id ? 'delivered' : 'sent_to_webhook' }
            : msg
        ));
      } catch (error) {
        console.error('Erro ao enviar mensagem:', error);
        setLocalMessages(prev => prev.filter(msg => msg.id !== tempId));
      } finally {
        setSendingMessage(false);
      }
    }, 0);
  };

  // Função para lidar com Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage().catch(err => console.error('[Chats] sendMessage error:', err));
    }
  };

  // Função para verificar consistência dos chats
  const handleVerifyChats = async () => {
    if (!verifyChats) return;
    
    setIsVerifying(true);
    setVerificationLogs([]);
    const logs: string[] = [];
    
    const addLog = (message: string) => {
      const timestamp = new Date().toLocaleTimeString('pt-BR');
      logs.push(`[${timestamp}] ${message}`);
      setVerificationLogs([...logs]);
      console.log(`[Verificação] ${message}`);
    };
    
    try {
      addLog('🔍 Iniciando verificação de consistência dos chats...');
      addLog(`📊 Total de chats exibidos: ${chats.length}`);
      
      const result = await verifyChats();
      setVerificationResult(result);
      
      if (result) {
        addLog(`✅ Verificação concluída`);
        addLog(`📈 Estatísticas:`);
        addLog(`   - Chats exibidos: ${result.displayedChats}`);
        addLog(`   - Chats na tabela: ${result.chatsInTable}`);
        addLog(`   - Chats sem registro na tabela: ${result.missingInTable.length}`);
        addLog(`   - Chats na tabela não exibidos: ${result.missingInDisplay.length}`);
        addLog(`   - Inconsistências: ${result.inconsistencies.length}`);
        if (result.createdChats && result.createdChats > 0) {
          addLog(`   - Chats criados automaticamente: ${result.createdChats}`);
        }
        if (result.fixedIssues && result.fixedIssues.length > 0) {
          addLog(`\n✅ PROBLEMAS CORRIGIDOS:`);
          result.fixedIssues.forEach((fix, index) => {
            addLog(`   ${index + 1}. ${fix}`);
          });
        }
        
        if (result.missingInTable.length > 0) {
          addLog(`\n⚠️ CHATS EXIBIDOS SEM REGISTRO NA TABELA (${result.missingInTable.length}):`);
          result.missingInTable.forEach((chat, index) => {
            addLog(`   ${index + 1}. Telefone: ${chat.phone} | Instância: ${chat.instance_id}`);
          });
        }
        
        if (result.missingInDisplay.length > 0) {
          addLog(`\n⚠️ CHATS NA TABELA NÃO EXIBIDOS (${result.missingInDisplay.length}):`);
          result.missingInDisplay.forEach((chat, index) => {
            addLog(`   ${index + 1}. ID: ${chat.id} | Telefone: ${chat.phone} | Instância: ${chat.instance_id}`);
          });
        }
        
        if (result.inconsistencies.length > 0) {
          addLog(`\n⚠️ INCONSISTÊNCIAS ENCONTRADAS (${result.inconsistencies.length}):`);
          result.inconsistencies.forEach((inconsistency, index) => {
            addLog(`   ${index + 1}. ${inconsistency.issue}`);
            if (inconsistency.details) {
              addLog(`      Detalhes: ${JSON.stringify(inconsistency.details, null, 2)}`);
            }
          });
        }
        
        if (result.missingInTable.length === 0 && result.missingInDisplay.length === 0 && result.inconsistencies.length === 0) {
          addLog(`\n✅ Tudo consistente! Não foram encontrados problemas.`);
        }
        
        const issues = [];
        if (result.missingInTable.length > 0) {
          issues.push(`${result.missingInTable.length} chat(s) exibidos sem registro na tabela`);
        }
        if (result.missingInDisplay.length > 0) {
          issues.push(`${result.missingInDisplay.length} chat(s) na tabela não exibidos`);
        }
        if (result.inconsistencies.length > 0) {
          issues.push(`${result.inconsistencies.length} inconsistência(s) encontrada(s)`);
        }
        
        if (issues.length > 0) {
          toast.warning(`Verificação concluída: ${issues.join(', ')}. Abra o log para detalhes.`, {
            action: {
              label: 'Ver Log',
              onClick: () => setShowVerificationLog(true),
            },
          });
        } else {
          toast.success('Verificação concluída: Tudo consistente!', {
            action: {
              label: 'Ver Log',
              onClick: () => setShowVerificationLog(true),
            },
          });
        }
        
        // Abrir o modal de log automaticamente se houver problemas
        if (issues.length > 0) {
          setShowVerificationLog(true);
        }
      }
    } catch (error) {
      addLog(`❌ Erro ao verificar chats: ${error}`);
      console.error('Erro ao verificar chats:', error);
      toast.error('Erro ao verificar chats. Veja o log para detalhes.', {
        action: {
          label: 'Ver Log',
          onClick: () => setShowVerificationLog(true),
        },
      });
      setShowVerificationLog(true);
    } finally {
      setIsVerifying(false);
    }
  };

  // Removido: Bloqueio de renderização para não-admins - agora membros também podem acessar

  // Fallback: Se não houver organização, mostrar mensagem
  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhuma organização selecionada</h3>
          <p className="text-muted-foreground">
            Selecione uma organização para acessar os chats.
          </p>
        </div>
      </div>
    );
  }

  if (instancesLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando instâncias...</p>
        </div>
      </div>
    );
  }

  if (!instances || instances.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhuma instância conectada</h3>
          <p className="text-muted-foreground">
            Conecte uma instância do WhatsApp para começar a usar os chats.
          </p>
          <div className="mt-4 text-sm text-muted-foreground">
            <p>Debug: Instâncias encontradas: {instances?.length || 0}</p>
            <p>Status: {instancesLoading ? 'Carregando...' : 'Carregado'}</p>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="flex flex-col -m-6 overflow-hidden" style={{ height: '100vh' }}>
      
      {/* Modal de Log de Verificação */}
      <Dialog open={showVerificationLog} onOpenChange={setShowVerificationLog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileTextIcon className="h-5 w-5" />
              Log de Verificação de Chats
            </DialogTitle>
            <DialogDescription>
              Detalhes da verificação de consistência entre chats exibidos e registros no banco de dados
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Resumo */}
            {verificationResult && (
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-semibold mb-2">📊 Resumo</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Chats exibidos:</span>
                    <span className="ml-2 font-medium">{verificationResult.displayedChats}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Chats na tabela:</span>
                    <span className="ml-2 font-medium">{verificationResult.chatsInTable}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sem registro na tabela:</span>
                    <span className="ml-2 font-medium text-orange-600">{verificationResult.missingInTable.length}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Não exibidos:</span>
                    <span className="ml-2 font-medium text-orange-600">{verificationResult.missingInDisplay.length}</span>
                  </div>
                  {verificationResult.createdChats && verificationResult.createdChats > 0 && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Chats criados automaticamente:</span>
                      <span className="ml-2 font-medium text-green-600">{verificationResult.createdChats}</span>
                    </div>
                  )}
                </div>
                {verificationResult.fixedIssues && verificationResult.fixedIssues.length > 0 && (
                  <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <div className="font-semibold text-green-700 dark:text-green-400 mb-1">✅ Problemas Corrigidos:</div>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {verificationResult.fixedIssues.map((fix, index) => (
                        <li key={index}>{fix}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Logs detalhados */}
            <div className="space-y-2">
              <h3 className="font-semibold">📝 Log Detalhado</h3>
              <ScrollArea className="h-[400px] w-full border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                <div className="font-mono text-xs space-y-1">
                  {verificationLogs.length > 0 ? (
                    verificationLogs.map((log, index) => (
                      <div key={index} className="whitespace-pre-wrap break-words">
                        {log}
                      </div>
                    ))
                  ) : (
                    <div className="text-muted-foreground">
                      {isVerifying ? 'Verificando...' : 'Nenhum log disponível. Execute uma verificação primeiro.'}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
            
            {/* Detalhes dos problemas */}
            {verificationResult && (
              <div className="space-y-4">
                {verificationResult.missingInTable.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-orange-600 mb-2">
                      ⚠️ Chats Exibidos Sem Registro na Tabela ({verificationResult.missingInTable.length})
                    </h3>
                    <ScrollArea className="h-[150px]">
                      <div className="space-y-1 text-sm">
                        {verificationResult.missingInTable.map((chat: any, index: number) => (
                          <div key={index} className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                            <span className="font-medium">Telefone:</span> {chat.phone} | 
                            <span className="font-medium ml-2">Instância:</span> {chat.instance_id}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
                
                {verificationResult.missingInDisplay.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-orange-600 mb-2">
                      ⚠️ Chats na Tabela Não Exibidos ({verificationResult.missingInDisplay.length})
                    </h3>
                    <ScrollArea className="h-[150px]">
                      <div className="space-y-1 text-sm">
                        {verificationResult.missingInDisplay.map((chat: any, index: number) => (
                          <div key={index} className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                            <span className="font-medium">ID:</span> {chat.id} | 
                            <span className="font-medium ml-2">Telefone:</span> {chat.phone} | 
                            <span className="font-medium ml-2">Instância:</span> {chat.instance_id}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
                
                {verificationResult.inconsistencies.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-orange-600 mb-2">
                      ⚠️ Inconsistências ({verificationResult.inconsistencies.length})
                    </h3>
                    <ScrollArea className="h-[150px]">
                      <div className="space-y-2 text-sm">
                        {verificationResult.inconsistencies.map((inc: any, index: number) => (
                          <div key={index} className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                            <div className="font-medium">{inc.issue}</div>
                            {inc.details && (
                              <pre className="mt-1 text-xs overflow-auto">
                                {JSON.stringify(inc.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Área principal sem scroll */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Sidebar com chats */}
        <div className={`border-r border-[#e2e8f0] flex-shrink-0 bg-white ${showMobileConversation && selectedChat ? "hidden md:flex md:flex-col md:w-80 lg:w-[400px]" : "flex flex-col w-full md:w-80 lg:w-[400px]"}`}>
          {/* Lista de chats */}
          <ChatList
            chats={chats}
            selectedChat={selectedChat}
            onChatSelect={(chat) => {
              setSelectedChat(chat);
              setShowMobileConversation(true);
            }}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isLoading={chatsLoading}
            contactTags={contactTags}
            onUpdateContactTags={updateContactTags}
            refreshTrigger={refreshTrigger}
            selectedInstance={selectedInstance}
            onRefetchChats={refetchChats}
            onContactAssigned={handleContactAssigned}
            onPrefetchChat={(unifiedHook as any).prefetchChat}
            operatorStatus={operatorStatus}
            onOperatorStatusChange={handleOperatorStatusChange}
          />
        </div>

        {/* Área de conversa */}
        <div className={`flex-1 min-w-0 flex-col min-h-0 overflow-hidden bg-gradient-to-b from-[#2563EB]/20 to-white ${showMobileConversation && selectedChat ? "flex" : "hidden md:flex"}`}>
        {selectedChat && isLoadingMessages ? (
          /* Full-panel skeleton while messages are loading — avoids showing header/input before content is ready */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-in fade-in duration-200">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              <span className="text-sm text-muted-foreground">Carregando mensagens…</span>
            </div>
          </div>
        ) : selectedChat ? (
          <>
            {/* Botão voltar mobile */}
            <div className="md:hidden flex items-center px-3 py-2 border-b border-[#e2e8f0] bg-white flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMobileConversation(false)}
                className="flex items-center gap-1 text-sm h-8 px-2 text-[#4a5565]"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>
            </div>
            {/* Header da conversa - FIXO */}
            <div className="flex-shrink-0">
              <ChatHeader
                chat={selectedChat}
                contactTags={selectedChatTags}
                onUpdateContactTags={updateContactTags}
                onContactAssigned={handleContactAssigned}
                onLoadFullHistory={handleLoadFullHistory}
                showAllMessages={showAllMessages}
                onCloseChatInstagram={selectedChat && ((selectedChat as any).full?.chat_id ?? (selectedChat as any).chat_id) ? (params) => unifiedHook.closeChat(params) : undefined}
                instanceIdFromParent={effectiveInstanceId}
              />
            </div>

            {/* Mensagens - SCROLLÁVEL */}
            <div className="flex-1 min-h-0 min-w-0 relative overflow-hidden">
              {/* Indicador de visualização completa */}
              {showAllMessages && (
                <div className="absolute top-0 left-0 right-0 z-10 bg-primary/10 border-b border-primary/20 px-6 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <History className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">
                      Visualizando histórico completo de todas as mensagens
                    </span>
                  </div>
                </div>
              )}
              <ScrollArea className={`h-full px-3 md:px-6 ${showAllMessages ? 'pt-20' : ''}`} style={{background:'linear-gradient(160deg,#e8eef8 0%,#f3f6fc 100%)'}} data-chat-scroll>
                <div className="space-y-6 w-full min-w-0" ref={messagesContainerRef} data-chat-area>
                  {/* Skeleton de carregamento */}
                  {isLoadingMessages && (
                    <div className="space-y-5 py-6 animate-in fade-in duration-300">
                      {/* Mensagem esquerda */}
                      <div className="flex items-end gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#255956]/20 animate-pulse shrink-0" />
                        <div className="space-y-1.5">
                          <div className="h-3 w-16 rounded bg-[#255956]/15 animate-pulse" />
                          <div className="h-10 w-48 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[4px] rounded-tr-[16px] bg-[#255956]/15 animate-pulse" />
                        </div>
                      </div>
                      {/* Mensagem direita */}
                      <div className="flex items-end gap-3 justify-end">
                        <div className="space-y-1.5 items-end flex flex-col">
                          <div className="h-3 w-12 rounded bg-slate-200 animate-pulse" />
                          <div className="h-10 w-64 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[4px] bg-white/80 border border-slate-100 animate-pulse" />
                        </div>
                        <div className="h-8 w-8 rounded-full bg-[#2563EB]/20 animate-pulse shrink-0" />
                      </div>
                      {/* Mensagem esquerda longa */}
                      <div className="flex items-end gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#255956]/20 animate-pulse shrink-0" />
                        <div className="space-y-1.5">
                          <div className="h-3 w-16 rounded bg-[#255956]/15 animate-pulse" />
                          <div className="h-14 w-72 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[4px] rounded-tr-[16px] bg-[#255956]/15 animate-pulse" />
                        </div>
                      </div>
                      {/* Mensagem direita curta */}
                      <div className="flex items-end gap-3 justify-end">
                        <div className="space-y-1.5 items-end flex flex-col">
                          <div className="h-3 w-12 rounded bg-slate-200 animate-pulse" />
                          <div className="h-10 w-40 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[4px] bg-white/80 border border-slate-100 animate-pulse" />
                        </div>
                        <div className="h-8 w-8 rounded-full bg-[#2563EB]/20 animate-pulse shrink-0" />
                      </div>
                      {/* Mensagem direita longa */}
                      <div className="flex items-end gap-3 justify-end">
                        <div className="space-y-1.5 items-end flex flex-col">
                          <div className="h-3 w-12 rounded bg-slate-200 animate-pulse" />
                          <div className="h-20 w-80 rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[4px] bg-white/80 border border-slate-100 animate-pulse" />
                        </div>
                        <div className="h-8 w-8 rounded-full bg-[#2563EB]/20 animate-pulse shrink-0" />
                      </div>
                    </div>
                  )}

                  {/* Indicador de carregamento de mais mensagens */}
                  {!isLoadingMessages && isLoadingMoreMessages && (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  )}
                  
                  {/* Referência para detectar scroll no topo */}
                  <div ref={messagesTopRef} />
                  
                  {!isLoadingMessages && messagesGroupedByDate.length > 0 ? (
                    messagesGroupedByDate.map((group) => (
                      <div key={group.dateKey} className="space-y-4">
                        <div className="flex items-center gap-3 my-1">
                          <div className="flex-1 h-px bg-[#e2e8f0]" />
                          <span className="text-[11px] text-[#94a3b8] font-normal shrink-0 select-none">
                            {group.dateLabel}
                          </span>
                          <div className="flex-1 h-px bg-[#e2e8f0]" />
                        </div>
                        {group.messages.map((message, index) => {
                          const isLastUnread = message.id === lastUnreadMessageId;
                          const firstViewedAt = (message as any).first_viewed_at;
                          const senderType = (message as any).sender_type;
                          const isFromClient = senderType === 'contact' ||
                            message.message_type === 'user' || message.message_type === 'messages.upsert';
                          const isUnread = isFromClient &&
                            (!firstViewedAt || firstViewedAt === null || firstViewedAt === "");
                          const isLastUnreadMarker = message.id === lastUnreadMessageId && isUnread;

                          return (
                            <div key={message.id || `msg-${group.dateKey}-${index}`}>
                              {isLastUnreadMarker && (
                                <div
                                  ref={unreadMarkerRef}
                                  className="flex items-center gap-2 my-4 py-2 px-4 bg-primary/10 border-t border-b border-primary/30"
                                >
                                  <div className="h-px flex-1 bg-primary/30" />
                                  <Badge variant="secondary" className="text-xs font-medium text-primary bg-primary/20 flex items-center gap-1.5">
                                    <span>{localUnreadCount > 0 ? `${localUnreadCount} mensagen${localUnreadCount !== 1 ? 's' : ''} não lida${localUnreadCount !== 1 ? 's' : ''}` : 'Mensagens não lidas'}</span>
                                  </Badge>
                                  <div className="h-px flex-1 bg-primary/30" />
                                </div>
                              )}
                              <ChatMessage
                                message={message}
                                instanceId={effectiveInstanceId}
                                phone={selectedChat?.phone}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))
                  ) : !isLoadingMessages ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground py-8">
                      <p>Nenhuma mensagem ainda</p>
                    </div>
                  ) : null}
                  {/* Bloco "Chat encerrado" */}
                  {closedInfo && (
                    <div className="my-6 w-full min-w-0 flex flex-col items-center gap-3 px-1">
                      {/* Divider com label centralizado */}
                      <div className="flex items-center gap-3 w-full">
                        <div className="flex-1 h-px bg-border" />
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full border bg-background text-[11px] font-medium text-muted-foreground shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          <span>Conversa encerrada</span>
                        </div>
                        <div className="flex-1 h-px bg-border" />
                      </div>

                      {/* Card de detalhes */}
                      <div className="w-full rounded-2xl border bg-muted/20 overflow-hidden">
                        {closedInfo.notes_closed && (
                          <div className="px-4 py-3 border-b">
                            <p className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-0.5">Motivo</p>
                            <p className="text-sm text-foreground break-words overflow-wrap-anywhere">
                              {closedInfo.notes_closed.replace(/\n/g, " — ")}
                            </p>
                          </div>
                        )}
                        <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="h-6 w-6 rounded-full bg-[#002172]/10 flex items-center justify-center shrink-0">
                              <User className="h-3.5 w-3.5 text-[#002172]" />
                            </div>
                            <span className="text-sm text-foreground truncate">{closerProfile?.name ?? "Usuário"}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                            <Clock className="h-3 w-3" />
                            <span>{format(new Date(closedInfo.closed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            </div>
            {/* Banner: instância do chat desativada ou excluída */}
            {isChatInstanceProblematic && !isInternalNoteMode && (
              <div className="mx-2 md:mx-5 mb-2 p-3 rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800/50">
                <div className="flex items-start gap-2 mb-2.5">
                  <WifiOff className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                      {!chatOwnInstanceData || chatOwnInstanceData.status === 'deleted'
                        ? 'Instância excluída'
                        : 'Instância desconectada'}
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-400 mt-0.5">
                      {!chatOwnInstanceData || chatOwnInstanceData.status === 'deleted'
                        ? 'A instância original deste chat foi excluída. Selecione outra instância para enviar mensagens.'
                        : 'A instância original deste chat está desconectada. Selecione outra instância para enviar mensagens.'}
                    </p>
                  </div>
                </div>
                <Select value={overrideInstanceId ?? ''} onValueChange={setOverrideInstanceId}>
                  <SelectTrigger className="w-full h-9 bg-white dark:bg-background border-orange-200 dark:border-orange-800/50 text-sm">
                    <SelectValue placeholder="Selecionar instância para envio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeInstancesForOverride.length === 0 ? (
                      <SelectItem value="__none__" disabled>Nenhuma instância ativa disponível</SelectItem>
                    ) : (
                      activeInstancesForOverride.map(inst => (
                        <SelectItem key={inst.id} value={inst.id}>
                          {(inst as any).instance_name || inst.id}
                          {inst.channel ? ` (${inst.channel})` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Banner: aguardando resposta do cliente após disparo */}
            {isBlockedByDisparo && (
              <div className="mx-2 md:mx-5 mb-2 p-3 rounded-xl border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800/50">
                <div className="flex items-start gap-2 mb-2.5">
                  <Megaphone className="h-4 w-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-violet-800 dark:text-violet-300">
                      Aguardando resposta do cliente
                    </p>
                    <p className="text-xs text-violet-700 dark:text-violet-400 mt-0.5">
                      Esta conversa foi iniciada por disparo. Mensagens livres só são permitidas após o cliente responder. Você pode enviar um novo template.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs"
                  onClick={() => setShowDisparoTemplateDialog(true)}
                >
                  <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                  Enviar Novo Template
                </Button>
              </div>
            )}

            {/* Banner: chat encerrado com opção de novo disparo via template */}
            {closedInfo && isZenviaOrMetaInstance && !isInternalNoteMode && (
              <div className="mx-2 md:mx-5 mb-2 p-3 rounded-xl border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800/50">
                <div className="flex items-start gap-2 mb-2.5">
                  <Megaphone className="h-4 w-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-violet-800 dark:text-violet-300">
                      Reiniciar conversa via template
                    </p>
                    <p className="text-xs text-violet-700 dark:text-violet-400 mt-0.5">
                      Este chat está encerrado. Para entrar em contato novamente, envie um template de disparo.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs"
                  onClick={() => setShowDisparoTemplateDialog(true)}
                >
                  <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                  Enviar Template de Disparo
                </Button>
              </div>
            )}

            <div className="flex-shrink-0 px-2 md:px-4 pt-2 pb-3 md:pb-4" data-input-container>
              <div className={`bg-white border rounded-2xl shadow-[0px_4px_24px_0px_rgba(0,33,114,0.10)] overflow-hidden ${isInternalNoteMode ? 'border-amber-200' : 'border-[#e2e8f0]'}`}>
                {/* Tab strip */}
                <div className={`flex items-stretch border-b px-4 gap-1 ${isInternalNoteMode ? 'border-amber-100 bg-[#fffcf0]' : 'border-[#f0f4f8]'}`}>
                  <button
                    type="button"
                    onClick={() => { setNoteText(""); setIsInternalNoteMode(false); }}
                    disabled={sendingMessage}
                    className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-all ${!isInternalNoteMode ? 'text-[#002172] border-[#002172] font-semibold' : 'text-[#94a3b8] border-transparent hover:text-[#64748b]'}`}
                  >
                    <Send className="h-3 w-3" />
                    Responder
                  </button>
                  <button
                    type="button"
                    onClick={() => { setNoteText(""); setIsInternalNoteMode(true); }}
                    disabled={sendingMessage || !selectedChat}
                    className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-all ${isInternalNoteMode ? 'text-amber-600 border-amber-400 font-semibold' : 'text-[#94a3b8] border-transparent hover:text-[#64748b]'}`}
                  >
                    <FileText className="h-3 w-3" />
                    Nota interna
                  </button>
                </div>

                {/* Note banner */}
                {isInternalNoteMode && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 text-[12px] font-medium text-amber-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    Esta mensagem é interna — não aparece para o cliente
                  </div>
                )}

                {/* Textarea */}
                <TooltipProvider delayDuration={300}>
                  <Tooltip open={!isInternalNoteMode && operatorStatus !== "online" ? undefined : false}>
                    <TooltipTrigger asChild>
                      <textarea
                        placeholder={isInternalNoteMode ? "Escreva sua anotação interna..." : isBlockedByDisparo ? "Aguardando resposta do cliente ao disparo..." : "Escreva sua mensagem..."}
                        className={`w-full min-h-[72px] max-h-40 resize-none border-0 outline-none px-4 py-3 text-[14px] leading-relaxed font-sans text-[#111827] ${
                          isInternalNoteMode ? 'bg-[#fffcf0] placeholder:text-amber-300' : 'bg-white placeholder:text-[#94a3b8]'
                        }`}
                        data-message-input={!isInternalNoteMode}
                        data-internal-note-input={isInternalNoteMode}
                        value={isInternalNoteMode ? noteText : messageText}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (isInternalNoteMode) {
                            setNoteText(newValue);
                          } else {
                            setMessageText(newValue);
                            if (newValue === '\\') {
                              setShowMacroMenu(true);
                              setMacroSearchQuery('');
                            } else if (newValue.startsWith('\\') && showMacroMenu) {
                              setMacroSearchQuery(newValue.slice(1));
                            } else if (!newValue.startsWith('\\') && showMacroMenu) {
                              setShowMacroMenu(false);
                              setMacroSearchQuery('');
                            }
                          }
                        }}
                        onKeyDown={handleKeyPress}
                        disabled={sendingMessage || !!closedInfo || (!isInternalNoteMode && isChatInstanceProblematic && !overrideInstanceId) || (!isInternalNoteMode && operatorStatus !== "online") || (!isInternalNoteMode && isBlockedByDisparo)}
                        ref={messageInputRef}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      Fique <span className="font-semibold text-green-400">Online</span> para enviar mensagens
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Bottom toolbar */}
                <div className={`flex items-center px-3 pb-2.5 pt-1.5 border-t ${isInternalNoteMode ? 'border-amber-100 bg-[#fffaeb]' : 'border-[#f0f4f8] bg-[#fafbfd]'}`} data-chat-input-buttons>
                  <div className="flex items-center gap-0.5 flex-1">
                    {/* Emoji */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 rounded-lg hover:bg-accent"
                          disabled={sendingMessage}
                        >
                          <Smile className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <div className="grid grid-cols-8 gap-2 max-h-64 overflow-y-auto">
                          {COMMON_EMOJIS.map((emoji, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => { insertEmoji(emoji); }}
                              className="text-2xl hover:bg-accent rounded p-1 transition-colors cursor-pointer"
                              disabled={sendingMessage}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Attach */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={isImageVideoOnlyMode ? "image/*,video/*" : "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"}
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={sendingMessage}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 rounded-lg hover:bg-accent"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sendingMessage || !selectedChat}
                      title="Anexar arquivo"
                    >
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                    </Button>

                    {/* Macros */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="hidden sm:flex h-8 w-8 rounded-lg hover:bg-accent"
                      onClick={() => setShowMacrosManager(true)}
                      disabled={sendingMessage}
                      title="Gerenciar Macros"
                    >
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>

                  {/* Send */}
                  <Button
                    onClick={() => sendMessage().catch(err => console.error('[Chats] sendMessage error:', err))}
                    disabled={(isInternalNoteMode ? !noteText.trim() : !messageText.trim()) || sendingMessage || !!closedInfo || (!isInternalNoteMode && isChatInstanceProblematic && !overrideInstanceId) || (!isInternalNoteMode && operatorStatus !== "online") || (!isInternalNoteMode && isBlockedByDisparo)}
                    className={`h-9 px-4 rounded-xl text-[13px] font-bold gap-1.5 ${
                      isInternalNoteMode
                        ? "bg-amber-400 hover:bg-amber-500 text-white"
                        : "bg-[#002172] hover:bg-[#001558] text-white shadow-[0_2px_10px_rgba(0,33,114,0.30)]"
                    }`}
                    title={isInternalNoteMode ? "Salvar anotação interna" : "Enviar mensagem para o cliente"}
                    data-send-button={!isInternalNoteMode}
                  >
                    {sendingMessage ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5" />
                        {isInternalNoteMode ? 'Salvar nota' : 'Enviar'}
                      </>
                    )}
                  </Button>
                </div>
              </div>

            </div>
            
            {/* Macro Menu - exibe quando "\" é digitado */}
            {showMacroMenu && (
              <MacroMenu
                open={showMacroMenu}
                onSelect={handleMacroSelect}
                onClose={() => {
                  setShowMacroMenu(false);
                  setMacroSearchQuery('');
                }}
                triggerRef={messageInputRef}
                searchQuery={macroSearchQuery}
                onCreateNew={() => setShowCreateMacroDialog(true)}
              />
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <ChatEmptyState />
          </div>
        )}
        </div>
      </div>
      
      {showMacrosManager && (
        <MacrosManagerDialog
          open={showMacrosManager}
          onOpenChange={setShowMacrosManager}
        />
      )}

      {showCreateMacroDialog && (
        <MacroDialog
          open={showCreateMacroDialog}
          onOpenChange={setShowCreateMacroDialog}
          mode="create"
        />
      )}

      {/* Dialog de template para disparo via Zenvia/Meta */}
      {selectedChat && currentOrg && (
        <ChatDisparoTemplateDialog
          open={showDisparoTemplateDialog}
          onOpenChange={setShowDisparoTemplateDialog}
          instanceId={chatOwnInstanceData?.id ?? (selectedChat as any)?.instance_id ?? ''}
          instanceName={(chatOwnInstanceData as any)?.instance_name ?? null}
          orgId={currentOrg.id}
          contactPhone={(selectedChat as any)?.phone ?? (selectedChat as any)?.origem_contato ?? ''}
          contactName={(selectedChat as any)?.contactName ?? (selectedChat as any)?.nomewpp ?? null}
          integrationType={chatOwnInstanceData?.integration_type ?? ''}
        />
      )}
    </div>
  );
}
